import * as THREE from "three";
import { MATS } from "./materials.js";
import { makeRng, worldUV } from "./builder.js";
import { LOT_EDGES, LOTS, Z, benchY, frontLineZ, groundY } from "./spec.js";
import { burrBush } from "./greenspace.js";

/**
 * Garden boundaries: the rear fence along the greenspace, the four side
 * fences between lots, gates, and a little low boundary at the front.
 *
 * Board panels are drawn cheaply: one thin "skin" box per 2.4 m bay carrying
 * the plank texture (UVs rotated so the seams run vertically and read as
 * boards), with a handful of proud battens per bay for board-on-board relief.
 * Only the pieces the player studies up close -- gates and the broken-board
 * bay -- get individual boards.  Collision is one box per straight run, never
 * per board.  The rear boundary is not boards at all; see chainRun.
 *
 * TRAVERSAL.  A 1.81 m fence is impassable to a 0.95 m jump, and the four side
 * fences between the lots still are 1.81 m, so each garden is still given a
 * deliberate ground-level way in and out (walk-through, no jump, so they work
 * in both directions):
 *
 *   lot1 (middle): east side gate ajar at (6.5, -10.45), ~1.4 m opening.
 *   lot0 (west):   a missing board in the shared fence x=-7.5 at z=-19.3,
 *         0.36 m wide and open to 0.90 m high (from lot1's garden); and a
 *         dug gap under the rear fence at (-17.55, -30), 0.38 m clearance
 *         (cat collision box is 0.32 m tall), out to the greenspace.
 *   lot2 (east):   east side gate ajar at (20.7, -10.45), ~1.0 m opening.
 *
 * The REAR boundary is no longer one of them and no longer has a gate.  It is
 * chain link at MESH_TOP -- under the jump -- so the whole 68 m of it is the
 * route: from anywhere in any of the three back gardens the cat hops onto the
 * top rail and steps down into the greenspace, and back the same way.  That is
 * a plain jump from a standing start; it does not need a run-up and it does
 * not need the charged pounce.  The route hint sits on the rail rather than on
 * the grass, because the rail is what has to be reachable for any of this to
 * be true.
 *
 * The lot0 burrow stays anyway.  It is the only rear crossing that works
 * without leaving the ground, and it is what keeps the greenspace connected
 * for anything that floods the level with a step-up and no jump.
 *
 * The remaining four side gates are shut and solid, so the gardens between
 * each other still feel private.
 */

const TOP = 1.81;           // collider top == cap top; "a 1.8 m fence"
const BOARD_Y0 = 0.05;      // boards float a whisker off the grass
const BOARD_TOP = 1.72;     // cap sits on this
const CAP_H = 0.09;
const CAP_D = 0.16;
const RAIL_Y = [0.38, 1.18];
const COL_T = 0.16;         // run collider thickness (covers cap overhang)
const BAY = 2.4;            // post spacing
const Z_GATE = -10.45;      // side gates sit just behind the houses' rear corners
const Z_FENCE_END = -10.0;  // side fences run a little forward of houseBack

/**
 * The rear boundary's height, and the number this whole module now turns on.
 *
 * Chain link at a bit under half a panel, and the height is the entire point:
 * MOVE.jumpHeight is 0.95 m, so at MESH_TOP the cat gets over the park
 * boundary ANYWHERE along its 68 m rather than through one gate -- which is
 * why there is no rear gate any more.  It has to land ON the top rail to do
 * it; there is not enough air over a COL_T-thick collider to cross it in one
 * arc at any speed the player has, so the move is a hop up and a step down.
 *
 * NOT the 0.905 that "half of 1.81" gives, and not 0.90 either, for three
 * measured reasons.
 *
 * The 0.95 m jump is not 0.95 m.  Integration is semi-implicit -- velocity
 * updated before position -- so the discrete arc apexes half a velocity step
 * below the analytic one: 0.905 m at 60 Hz, 0.882 at 40, 0.862 at 30, and
 * 0.814 at the 20 Hz main.js's dt clamp bottoms out at.  Driven at a 0.905 m
 * fence the real Player fails at 60 Hz and clears it at 144, which is the
 * worst kind of boundary to build -- one whose passability is a property of
 * the player's monitor.  0.84 clears the arc at every rate down to ~25 Hz.
 *
 * reach.mjs will not flood a rise it cannot make from a sample that is
 * allowed to jump at all, and its budget falls off with horizontal reach: the
 * nearest such sample is two cells (0.2 m) out, worth 0.876 m.  A boundary the
 * project's own reachability tool reads as a wall is a boundary that quietly
 * becomes one.
 *
 * And 0.8475 is already this level's ceiling for the move -- the last rung of
 * the shed ladder, "the established big but makeable jump" (hero/shed.js).
 * The way into the park gets walked several times a session and has no
 * business being harder than the hardest optional climb in the garden.
 *
 * Still a fence, mind: driven at it with jump never pressed, the cat crosses
 * 0 times in 66 approaches.
 */
const MESH_TOP = 0.84;
const MESH_HEM = 0.05;      // fabric hangs to here; the hem wire runs on it
const MESH_T = 0.008;       // a woven skin, thick enough to have two faces
const MESH_TILE = 0.24;     // 4 diamonds per texture tile -> a 60 mm mesh
const CHAIN_BAY = 3.0;      // line posts, spaced as chain link really is
const POST_R = 0.024;
const RAIL_R = 0.019;
const WIRE_R = 0.007;

/**
 * How far a piece must move to stand on `level` rather than on its bench.
 *
 * MODULE_GRADE.fences is benchY, which is the right call for a fence -- bays
 * are built level, never tilted -- but benchY is the OLD stepped pad and the
 * Builder reads it at each piece's own centre.  So a run that wants a level
 * of its own has every piece cancel the grade it is about to be given and add
 * that instead, which also makes a run immune to being cut across the step:
 * the two posts either side of GRADE.line get 0.80 m of different bench and
 * land at exactly the same height.  Pass no level and nothing changes, which
 * is what the rear fence and the front boundaries want -- their ground is the
 * bench.
 */
const lift = (level, x, z) => (level == null ? 0 : level - benchY(x, z));

// What a panel may do to the grass before it stops looking like a fence.  A
// gap under the boards reads as broken long before a sunk one reads as sunk,
// hence the asymmetry.
const MAX_FLOAT = 0.10;
const MAX_BURY = 0.15;

/**
 * A boundary fence stepping down the ramp.
 *
 * The gardens sit a terrace below the street, and the whole of that drop used
 * to happen on one line at GRADE.line -- so a run reaching up the side of a
 * house was built as two, one on each terrace, or it would have hung 0.80 m
 * clear of the ground at one end with a collider to match.  The drop is now a
 * 13 m ramp (spec.terraceY), so where a run should break is no longer a fixed
 * line but a question about the ground: cut it while a single level would
 * leave a bay floating or buried, place each piece at the terrain height at
 * its own middle, and let the run step down the slope in bays the way a real
 * fence does.
 *
 * As the side fences stand today that yields ONE section per run: they stop
 * at z = -10, where the ramp has only 0.03 m left to fall, so a break there
 * would be a step nobody could see in a fence that has no reason to have one.
 * What the old code got wrong was never the number of pieces -- it was that
 * the near piece stood on the bench, 0.79 m above its own grass.
 */
function sideRun(b, edge, z0, z1, o) {
  let a0 = z0;
  while (a0 < z1 - 1e-4) {
    let a1 = z1;
    // Halving rather than a fixed bay length: a run mostly on the flat gets
    // to stay one run, and only the part on the slope pays for the slope.
    while (a1 - a0 > BAY / 2 && !sits(edge, a0, a1)) a1 = a0 + (a1 - a0) / 2;
    run(b, "z", edge, a0, a1, { ...o, level: groundY(edge, (a0 + a1) / 2) });
    a0 = a1;
  }
}

/** Would one level, read at its middle, suit the whole of this section? */
function sits(edge, z0, z1) {
  const y = groundY(edge, (z0 + z1) / 2);
  const n = Math.ceil((z1 - z0) / 0.4);
  for (let i = 0; i <= n; i++) {
    const g = groundY(edge, z0 + ((z1 - z0) * i) / n);
    if (y - g > MAX_FLOAT || g - y > MAX_BURY) return false;
  }
  return true;
}

// ---- axis plumbing: runs go along "x" (rear fence) or "z" (side fences) ---
const XZ = (axis, a, c) => (axis === "x" ? [a, c] : [c, a]);
const WD = (axis, len, t) => (axis === "x" ? [len, t] : [t, len]);

function blockA(b, axis, a, c, y, len, h, t, mat) {
  const [x, z] = XZ(axis, a, c);
  const [w, d] = WD(axis, len, t);
  b.block(x, y, z, w, h, d, mat, { solid: false });
}

function colliderA(b, axis, a, c, y, len, h, t, mat) {
  const [x, z] = XZ(axis, a, c);
  const [w, d] = WD(axis, len, t);
  b.collider(x, y, z, w, h, d, mat);
}

/**
 * A box whose plank texture runs vertically.  worldUV projects the texture
 * with its 5 plank rows stacked in Y, which reads as lap siding; swapping u/v
 * turns the rows into ~0.14 m wide vertical boards -- exactly a board fence.
 */
function boardBox(b, x, y, z, w, h, d, mat) {
  const g = new THREE.BoxGeometry(w, h, d).toNonIndexed();
  g.translate(x, y + h / 2, z);
  worldUV(g, 0.7);
  const uv = g.attributes.uv;
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i);
    uv.setX(i, uv.getY(i));
    uv.setY(i, u);
  }
  b.add(g, mat, { tile: 0 });
}

function boardBoxA(b, axis, a, c, y, len, h, t, mat) {
  const [x, z] = XZ(axis, a, c);
  const [w, d] = WD(axis, len, t);
  boardBox(b, x, y, z, w, h, d, mat);
}

function postAt(b, x, z, mat, o = {}) {
  const h = o.h ?? 1.8;
  const y = o.y ?? 0;
  boardBox(b, x, y, z, 0.1, h, 0.1, mat);
  b.block(x, y + h, z, 0.15, 0.045, 0.15, mat, { solid: false });
  // Posts inside a run are covered by the run's collider; only free-standing
  // gate posts that border an opening need their own box.
  if (o.collider) b.collider(x, y, z, 0.13, h + 0.04, 0.13, mat);
}

/**
 * A straight fence run: posts, capped bays with two rails and staggered
 * battens on both faces, and ONE collider for the whole run.
 *
 * o.y0 raises the board bottoms (the dug gap under the rear fence) and
 * o.colY0 raises the collider floor with them so what you see is what blocks.
 * o.level stands the whole run -- collider included, or the cat would walk
 * through it or be stopped in mid-air -- at one height of its own.
 */
function run(b, axis, c, a0, a1, o) {
  const mat = o.mat;
  const trimMat = o.trimMat ?? o.mat;
  const y0 = o.y0 ?? BOARD_Y0;
  const len = a1 - a0;
  const bays = Math.max(1, Math.round(len / BAY));
  const s = len / bays;
  const dy = (a) => lift(o.level, ...XZ(axis, a, c));

  const colY0 = o.colY0 ?? 0;
  const mid = (a0 + a1) / 2;
  colliderA(b, axis, mid, c, colY0 + dy(mid), len, TOP - colY0, COL_T, mat);

  for (let i = 0; i <= bays; i++) {
    if ((i === 0 && o.skipStartPost) || (i === bays && o.skipEndPost)) continue;
    const a = a0 + i * s;
    const [px, pz] = XZ(axis, a, c);
    postAt(b, px, pz, trimMat, { y: dy(a) });
  }

  for (let i = 0; i < bays; i++) {
    const b0 = a0 + i * s;
    const m = b0 + s / 2;
    boardBoxA(b, axis, m, c, y0 + dy(m), s, BOARD_TOP - y0, 0.04, mat); // skin
    blockA(b, axis, m, c, BOARD_TOP + dy(m), s, CAP_H, CAP_D, trimMat); // cap
    for (const ry of RAIL_Y)
      blockA(b, axis, m, c, ry + dy(m), s, 0.09, 0.09, mat);
    // proud battens, staggered per face, for board-on-board relief
    const nb = Math.max(3, Math.floor(s / 0.6));
    const p = s / nb;
    for (let k = 0; k < nb; k++) {
      for (const side of [1, -1]) {
        const a = b0 + p * (k + 0.5) + side * p * 0.25;
        if (a < a0 + 0.14 || a > a1 - 0.14) continue;   // keep clear of posts
        boardBoxA(b, axis, a, c + side * 0.034, y0 + dy(a), 0.16,
                  BOARD_TOP - y0, 0.028, mat);
      }
    }
  }
}

/**
 * A straight run of chain link: line posts, a top rail, the fabric, a tension
 * wire along the hem, and ONE collider -- the same bargain run() makes.
 *
 * Deliberately not a mode of run().  A board run is a skin, a cap, two rails
 * and a scatter of battens per bay, and a chain-link run is four continuous
 * members with a bay spacing that only decides where the posts land; sharing
 * one function would have meant a parameter for every line of it.  It also
 * only ever runs along x at Z.backFence, so it skips the axis plumbing the
 * side fences need, and it takes no `level`: this line lives entirely behind
 * GRADE.line, where the bench and the ground are the same thing.
 *
 * The collider keeps run()'s full COL_T rather than shrinking to the 48 mm of
 * an actual post, because it is no longer only a wall.  At MESH_TOP the cat is
 * meant to land on top of this, and 0.16 m of box under a 0.24 m cat is the
 * difference between a perch and a tightrope; the 56 mm that hangs outside the
 * posts either side is invisible and buys the landing its tolerance.  It stops
 * at the rail and not at the post caps 25 mm above it, so the perch is one
 * continuous line rather than a rail with a bump every 3 m.
 *
 * o.y0 lifts the fabric's hem and o.colY0 the collider's floor with it, which
 * is the dug gap at lot0 -- same contract as run().
 */
function chainRun(b, z, x0, x1, o = {}) {
  const y0 = o.y0 ?? MESH_HEM;
  const colY0 = o.colY0 ?? 0;
  const len = x1 - x0;
  const mid = (x0 + x1) / 2;
  const fabricTop = MESH_TOP - RAIL_R * 2;

  b.collider(mid, colY0, z, len, MESH_TOP - colY0, COL_T);

  const bays = Math.max(1, Math.round(len / CHAIN_BAY));
  const s = len / bays;
  for (let i = 0; i <= bays; i++) {
    if ((i === 0 && o.skipStartPost) || (i === bays && o.skipEndPost)) continue;
    const x = x0 + i * s;
    // 8 segments, not the default 10: at 48 mm across, the two facets that
    // buys are smaller than the wire hanging off the post.
    b.post(x, 0, z, POST_R, MESH_TOP + 0.02, MATS.metalDark,
           { segments: 8, solid: false });
    b.post(x, MESH_TOP + 0.02, z, POST_R * 1.2, 0.025, MATS.metalDark,
           { segments: 8, solid: false });            // the loop cap
  }

  // The top rail carries the fabric and is also what the cat stands on, so it
  // finishes exactly at MESH_TOP -- bar() measures from the underside.
  b.bar(mid, fabricTop, z, RAIL_R, len, "x", MATS.metalDark,
        { segments: 8, solid: false });
  // The tension wire threaded through the hem: without it the fabric's bottom
  // edge is a straight cut line and the whole thing reads as a printed sheet.
  if (o.hemWire !== false) {
    b.bar(mid, y0 - WIRE_R, z, WIRE_R, len, "x", MATS.metalDark,
          { segments: 6, solid: false });
  }

  const g = new THREE.BoxGeometry(len, fabricTop - y0, MESH_T);
  g.translate(mid, (y0 + fabricTop) / 2, z);
  // A box and not a plane: the cat walks both sides of this line, and two
  // outward-facing skins 8 mm apart give it two lit faces without the material
  // having to go DoubleSide.  They take the same world-projected UVs, so the
  // alphaTest cuts both in exactly the same place and the diamonds line up.
  b.add(g, MATS.chainLink, { tile: MESH_TILE });
}

/**
 * One bay built from real, individual overlapping boards, with a gap where a
 * board has broken out at the bottom: the cat's way between lot0 and lot1.
 * Opening: o.g0..o.g1 wide, ground to 0.90 m; the lintel collider above it
 * keeps the rest of the panel solid.
 */
function boardGapBay(b, axis, c, a0, a1, o) {
  const gapTop = 0.9;
  colliderA(b, axis, (a0 + o.g0) / 2, c, 0, o.g0 - a0, TOP, COL_T, o.mat);
  colliderA(b, axis, (o.g1 + a1) / 2, c, 0, a1 - o.g1, TOP, COL_T, o.mat);
  colliderA(b, axis, (o.g0 + o.g1) / 2, c, gapTop, o.g1 - o.g0, TOP - gapTop,
            COL_T, o.mat);

  blockA(b, axis, (a0 + a1) / 2, c, BOARD_TOP, a1 - a0, CAP_H, CAP_D, o.trimMat);
  blockA(b, axis, (a0 + a1) / 2, c, RAIL_Y[1], a1 - a0, 0.09, 0.09, o.mat);
  // the lower rail is snapped through at the opening
  blockA(b, axis, (a0 + o.g0) / 2, c, RAIL_Y[0], o.g0 - a0 - 0.02, 0.09, 0.09, o.mat);
  blockA(b, axis, (o.g1 + a1) / 2, c, RAIL_Y[0], a1 - o.g1 - 0.02, 0.09, 0.09, o.mat);

  const n = Math.round((a1 - a0) / 0.132);
  const p = (a1 - a0) / n;
  for (let i = 0; i < n; i++) {
    const a = a0 + p * (i + 0.5);
    // any board that so much as overlaps the opening loses its lower half, so
    // the visible hole is never narrower than the collider gap
    const broken = a > o.g0 - 0.07 && a < o.g1 + 0.07;
    const y0 = broken ? gapTop + (i % 3 ? 0.02 : 0.14) : BOARD_Y0;
    boardBoxA(b, axis, a, c + (i % 2 ? 0.017 : -0.017), y0, 0.15,
              BOARD_TOP - y0, 0.034, o.mat);
  }

  // the kicked-out board, leaning against the lot1 side of the fence
  const g = new THREE.BoxGeometry(0.15, 1.3, 0.03).toNonIndexed();
  g.rotateZ(0.38);
  g.rotateY(0.25);
  g.translate(-7.26, 0.64, -18.5);
  b.add(g, o.mat, {});
}

/** A ledged-and-braced timber gate leaf, boards + two rails + a diagonal. */
function gateLeaf(b, axis, c, a0, a1, o = {}) {
  const mat = o.mat ?? MATS.timber;
  const y0 = 0.07;
  const top = 1.6;
  const span = a1 - a0;
  const n = Math.max(4, Math.round(span / 0.135));
  const p = span / n;
  const dy = (a) => lift(o.level, ...XZ(axis, a, c));
  const mid = (a0 + a1) / 2;
  for (let i = 0; i < n; i++) {
    const a = a0 + p * (i + 0.5);
    boardBoxA(b, axis, a, c, y0 + dy(a), p - 0.014, top - y0, 0.032, mat);
  }
  for (const ry of [0.28, 1.16]) {
    blockA(b, axis, mid, c + 0.034, ry + dy(mid), span - 0.04, 0.1, 0.035, mat);
  }
  const rise = 0.86;
  const gLen = Math.hypot(span, rise) - 0.18;
  const gg = (axis === "x"
    ? new THREE.BoxGeometry(gLen, 0.1, 0.035)
    : new THREE.BoxGeometry(0.035, 0.1, gLen)).toNonIndexed();
  const ang = Math.atan2(rise, span);
  if (axis === "x") gg.rotateZ(ang); else gg.rotateX(-ang);
  const [gx, gz] = XZ(axis, mid, c + 0.034);
  gg.translate(gx, 0.9 + dy(mid), gz);
  b.add(gg, mat, {});
  if (o.hingeEnd != null) {
    const inward = o.hingeEnd === a0 ? 0.08 : -0.08;
    for (const hy of [0.32, 1.14]) {
      blockA(b, axis, o.hingeEnd + inward, c + 0.05, hy + dy(o.hingeEnd),
             0.16, 0.05, 0.045, MATS.metalDark);
    }
    const latchEnd = o.hingeEnd === a0 ? a1 : a0;
    blockA(b, axis, latchEnd + (o.hingeEnd === a0 ? -0.08 : 0.08), c + 0.05,
           0.92 + dy(latchEnd), 0.1, 0.06, 0.05, MATS.metalDark);
  }
}

/**
 * A gate across the passage between a house wall and its boundary fence.
 * Shut: leaf drawn closed, one collider sealing the full passage (1.63 m
 * high -- above the 0.95 m jump, so it genuinely blocks).  Ajar: the leaf is
 * swung back through ~95 degrees to lie along the fence inside the garden,
 * and only the leaf and its hinge post collide, leaving a ~1 m+ walkway.
 */
function sideGate(b, g) {
  const sgn = Math.sign(g.fence - g.wall);
  const hingeX = g.fence - sgn * 0.13;
  const wallPostX = g.wall + sgn * 0.05;
  // The passage a side gate stands in is one of the few places the ramp is
  // out in the open, and Z_GATE sits 1 m up it: the bench says 0 here and the
  // grass is 0.78 m below that.  A gate is one object and hangs at one level,
  // so the whole assembly -- posts, leaf, collider -- takes the ground at the
  // gate.  Ungraded it floated, and a floating collider is worse than a
  // floating gate: the shut ones stopped sealing their passage at all.
  const level = groundY(g.fence, Z_GATE);
  const dy = lift(level, g.fence, Z_GATE);
  postAt(b, hingeX, Z_GATE, g.trim, { h: 1.86, collider: g.ajar, y: dy });
  boardBox(b, wallPostX, dy, Z_GATE, 0.08, 1.68, 0.08, g.trim);

  if (g.ajar) {
    const len = Math.abs(hingeX - wallPostX) - 0.12;
    const leafX = g.fence - sgn * 0.22;
    const z1 = Z_GATE - 0.06;
    gateLeaf(b, "z", leafX, z1 - len, z1, { hingeEnd: z1, level });
    b.collider(leafX, dy, z1 - len / 2, 0.12, 1.62, len + 0.1, MATS.timber);
  } else {
    const lo = Math.min(hingeX, wallPostX) + 0.06;
    const hi = Math.max(hingeX, wallPostX) - 0.06;
    gateLeaf(b, "x", Z_GATE, lo, hi, { hingeEnd: sgn > 0 ? hi : lo, level });
    b.collider((g.fence + g.wall) / 2, dy, Z_GATE,
               Math.abs(g.fence - g.wall) + 0.1, 1.63, 0.12, MATS.timber);
  }
}

/** d(frontLineZ)/dx, numerically, so it tracks spec.js if the crescent moves. */
function frontSlope(x) {
  return frontLineZ(x + 0.5) - frontLineZ(x - 0.5);
}

/**
 * Split [x0, x1] into pieces short enough to lay along the front line.
 *
 * The line bends: across lot 0's western picket it drops 1.1 m in 3.1 m of x.
 * One box cannot follow that, and the run has to, or it ends up half on the
 * lawn and half on the pavement -- which is exactly where these were.
 *
 * Segments are capped on their z-run rather than their length, the same rule
 * road.js's kerb uses, so they shorten where the curve is steepest and stay
 * long where it is nearly straight.
 */
function frontSegments(x0, x1, maxZRun = 0.22) {
  const out = [];
  let a = x0;
  while (a < x1 - 0.02) {
    const step = Math.min(1.5,
      Math.max(0.3, maxZRun / Math.max(Math.abs(frontSlope(a)), 1e-6)));
    const end = Math.min(a + step, x1);
    out.push([(a + end) / 2, end - a]);
    a = end;
  }
  return out;
}

/**
 * A continuous horizontal member along the front line: rail, hedge, kerb-like
 * anything.  Each segment is yawed to the local tangent and secant-scaled so
 * it still spans its slice of x once rotated, which is what keeps neighbours
 * butted instead of stepping.  The collider stays axis-aligned -- collision
 * only does AABBs -- and is grown in z by the segment's own run so it covers
 * the box it stands for.
 */
function frontBar(b, x0, x1, y, h, d, mat, opts = {}) {
  for (const [xc, w] of frontSegments(x0, x1)) {
    const th = Math.atan(frontSlope(xc));
    const z = frontLineZ(xc);
    const g = new THREE.BoxGeometry(w / Math.cos(th) + 0.04, h, d);
    g.rotateY(-th);
    g.translate(xc, y + h / 2, z);
    b.add(g, mat, {
      tile: opts.tile ?? 0.55,
      climb: opts.climb,
      collider: opts.solid === false ? null
        : { x: xc, y, z, w: w + 0.04, h,
            d: d + 0.04 + w * Math.abs(frontSlope(xc)) },
    });
  }
}

/** Knee-high white picket in front of lot0.  0.56 m: any cat clears it. */
function picketRun(b, x0, x1) {
  // One collider for the run, at the height a cat actually meets; the rails
  // and pales above it are decoration and carry none of their own.
  // climb: true is the painted-wood opt-in -- trim cannot be climbable by
  // material, or every house's window surrounds would come with it.
  frontBar(b, x0, x1, 0, 0.56, 0.09, MATS.trim, { tile: 0, climb: true });
  for (const px of [x0, (x0 + x1) / 2, x1]) {
    b.block(px, 0, frontLineZ(px), 0.07, 0.62, 0.07, MATS.trim, { solid: false });
  }
  for (const ry of [0.14, 0.4]) {
    frontBar(b, x0, x1, ry, 0.055, 0.035, MATS.trim, { solid: false });
  }
  const n = Math.round((x1 - x0) / 0.15);
  const p = (x1 - x0) / n;
  for (let i = 0; i < n; i++) {
    const px = x0 + p * (i + 0.5);
    b.block(px, 0.06, frontLineZ(px), 0.07, 0.52, 0.045, MATS.trim, { solid: false });
  }
}

/** Knee-high hedge in front of lot2; also trivially jumpable. */
function hedgeRun(b, x0, x1) {
  frontBar(b, x0, x1, 0, 0.46, 0.62, MATS.hedge, { tile: 0.8 });
  frontBar(b, x0 + 0.14, x1 - 0.14, 0.32, 0.2, 0.48, MATS.hedge,
           { solid: false, tile: 0.8 });
}

// ---------------------------------------------------------------------------
export function build(b) {
  const WARM = { mat: MATS.fence, trimMat: MATS.timber };
  const GREY = { mat: MATS.fenceGrey, trimMat: MATS.fenceGrey };
  const ZB = Z.backFence;

  // ---- rear boundary: black chain link at MESH_TOP, unbroken from corner to
  // corner.  It is the park fence, so it is the same fence for all three lots
  // and it has no gate: the height is the way through ----------------------
  chainRun(b, ZB, -34.0, -18.1);

  // the dug gap: this bay's fabric stops 0.38 m up (cat box is 0.32 m tall and
  // walking never lifts unless the direct move is already blocked, so it walks
  // straight under); the collider floor is raised to match.  Kept even though
  // the fence above it is now hoppable -- it is lot0's own way out at ground
  // level, it works crouched and carrying, and a scrape under chain link is if
  // anything more believable than one under boards.
  chainRun(b, ZB, -18.1, -17.0, {
    y0: 0.38, colY0: 0.38, hemWire: false,
    skipStartPost: true, skipEndPost: true,
  });
  b.slab(-17.55, 0.004, ZB, 1.25, 1.6, MATS.soil, { tile: 0.9 });
  b.sphere(-17.95, -0.02, ZB + 0.5, 0.16, MATS.mulch, { squash: 0.45 });
  b.sphere(-17.15, -0.02, ZB - 0.45, 0.13, MATS.mulch, { squash: 0.45 });
  // The hem the digging tore off its tension wire, rolled back on itself
  // against the garden face -- what stops the lifted fabric reading as a
  // suspiciously clean straight cut.  Its lowest corner lands at 0.394 and
  // never below 0.38: the visible gap has to be at least the one the collider
  // gives, or the cat walks through something it can see.  Rotated, so the
  // world-projected diamonds no longer follow it -- at 0.34 m of curled wire
  // nobody reads the weave either way.
  const flap = new THREE.BoxGeometry(0.34, 0.19, MESH_T).toNonIndexed();
  flap.rotateZ(0.42);
  flap.rotateY(0.22);
  flap.translate(-17.72, 0.55, ZB + 0.05);
  b.add(flap, MATS.chainLink, { tile: MESH_TILE });
  b.mark("route:lot0-burrow", -17.55, 0, ZB);

  chainRun(b, ZB, -17.0, 34.0);
  // Standing where the rear gate used to hang, and marked on the RAIL rather
  // than on the grass -- the opposite of the side gates below, for the same
  // reason: a route hint belongs where the route is.  This one is a hop, the
  // top rail is the thing that has to be gettable to, and putting the mark on
  // it makes reach.mjs answer exactly that question.  NOT REACHED here means
  // the park boundary has quietly gone back to being a wall.
  b.mark("route:rear-fence-hop", 0.4, MESH_TOP, ZB);

  // ---- side fences ---------------------------------------------------------
  sideRun(b, LOT_EDGES[0], -30, Z_FENCE_END, WARM);

  // lot0/lot1 boundary carries the missing-board gap at z=-19.3
  run(b, "z", LOT_EDGES[1], -30, -20.4, WARM);
  boardGapBay(b, "z", LOT_EDGES[1], -20.4, -18.0,
              { ...WARM, g0: -19.48, g1: -19.12 });
  sideRun(b, LOT_EDGES[1], -18.0, Z_FENCE_END, WARM);
  b.mark("route:lot0-board-gap", LOT_EDGES[1], 0, -19.3);

  sideRun(b, LOT_EDGES[2], -30, Z_FENCE_END, GREY);  // one weathered boundary
  sideRun(b, LOT_EDGES[3], -30, Z_FENCE_END, WARM);

  // ---- side gates (two ajar, four shut) ------------------------------------
  const gates = [
    { fence: LOT_EDGES[0], wall: LOTS[0].house.minX, ajar: false, trim: MATS.timber },
    { fence: LOT_EDGES[1], wall: LOTS[0].house.maxX, ajar: false, trim: MATS.timber },
    { fence: LOT_EDGES[1], wall: LOTS[1].house.minX, ajar: false, trim: MATS.timber },
    { fence: LOT_EDGES[2], wall: LOTS[1].house.maxX, ajar: true, trim: MATS.fenceGrey },
    { fence: LOT_EDGES[2], wall: LOTS[2].house.minX, ajar: false, trim: MATS.fenceGrey },
    { fence: LOT_EDGES[3], wall: LOTS[2].house.maxX, ajar: true, trim: MATS.timber },
  ];
  for (const g of gates) sideGate(b, g);
  // marked on the ramp, not on the bench: these two are the ways into the
  // back gardens and a hint 0.78 m in the air is a hint about nothing
  for (const [name, x] of [["route:lot1-side-gate", 6.4],
                           ["route:lot2-side-gate", 20.7]]) {
    b.mark(name, x, lift(groundY(x, Z_GATE), x, Z_GATE), Z_GATE);
  }

  // ---- low front boundaries; the rest of the frontage stays open -----------
  // Only the x span is stated: these sit ON the property line, so their z
  // comes from frontLineZ and bends with the street.  They used to be flat at
  // 13.55, which the crescent had left standing on the pavement at one end of
  // lot 0 and out in the road along lot 2.
  // lot0 picket, split at the front-door path (doorX = -17.65)
  picketRun(b, -21.42, -18.3);
  picketRun(b, -17.0, -14.8);
  // lot2 hedge pieces, clear of its door path (x=11.35) and driveway
  hedgeRun(b, 7.56, 10.7);
  hedgeRun(b, 20.3, 21.44);

  // Burr bushes bookending lot2's hedges -- greenspace.js's burrBush, the
  // one snagging plant the whole level shares (its header carries the
  // fairness argument; a front-garden variant would un-teach it).  A hedge
  // going to seed at its untrimmed ends is what this frontage would really
  // grow, and it puts the hazard where the front gardens' one open question
  // lives: cut the corner past the hedge, or walk the walk.
  //
  // Both spots are argued, and both were CHECKED IN FRAME, not just in
  // numbers -- the west one was first laid 0.8 m inside frontLineZ at
  // x 6.95, where the coordinates all cleared and the screenshot showed it
  // half-hidden behind the frontage tree yards.js plants at (8.4, 8.9): a
  // hazard a trunk can eclipse fails the only rule this feature has.  It
  // stands at the lot corner in the open now, a body-length clear of that
  // tree's crown and its mulch ring, marking the gap a cat cutting from
  // the hero lawn into lot2 actually crosses.  The drive-side one sits
  // 0.8 m inside the line at the hedge's driveway end.  Both are metres
  // clear of the door path at x 11.35 and a street away from every solo
  // place a brain routes to (sunspot, the nearest, is 7 m off).  Their own
  // rng stream, like the greenspace's, so the ornament of two bushes
  // cannot reshuffle anything already placed.
  const burrRng = makeRng(0xb1122ef);
  burrBush(b, burrRng, 6.1, 8.6, "front-west");
  burrBush(b, burrRng, 19.85, frontLineZ(19.85) - 0.8, "front-drive");
}
